/**
 * A discipline's sample projects: list them, and open the user's own copy of one (made the first
 * time, reopened after). Used by the lists whose stores do not carry samples themselves.
 */

import { useEffect, useState } from 'react';
import { useAuthStore } from './auth';

export interface SampleMeta {
  key: string;
  name: string;
  summary: string;
  teaches: string[];
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** The samples at `${base}/samples`; empty if there are none or the call fails. */
export function useSampleCatalogue(base: string): SampleMeta[] {
  const [samples, setSamples] = useState<SampleMeta[]>([]);
  useEffect(() => {
    let live = true;
    fetch(`${base}/samples`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((list: SampleMeta[]) => {
        if (live) setSamples(list);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [base]);
  return samples;
}

/** Opens the user's copy of a sample; resolves to the response body, or throws its detail. */
export async function openSampleAt<T>(base: string, key: string): Promise<T> {
  const response = await fetch(`${base}/samples/${encodeURIComponent(key)}`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => ({}));
    throw new Error(detail.detail ?? `Could not open the sample (${response.status})`);
  }
  return (await response.json()) as T;
}
